import React, { useContext, useEffect } from 'react';
import { Network } from 'lucide-react';
import { AssetTabs } from '../../components/assets/AssetTabs';
import { SubdomainsTable } from '../../components/assets/SubdomainsTable';
import { AssetsFilters } from '../../components/assets/AssetsFilters';
import { AssetsMetrics } from '../../components/assets/AssetsMetrics';
import { ExportButton, AddAssetButton } from '../../components/assets/AssetActionButtons';
import { useTeams } from '../../contexts/TeamsContext';
import { AssetsApi, Subdomain, SubdomainsListRequest } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { AuthContext } from '../../contexts/AuthContext';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

// Helper function to parse JSON data safely
const parseJsonData = <T extends object>(data: string | null | undefined): T | null => {
  if (!data) return null;
  try {
    // Parse string or return as is if already an object
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    // Ensure the result is treated as the expected type
    return parsed as T;
  } catch (e) {
    console.error('Error parsing data:', e);
    return null;
  }
};

// Define interface for parsed parent domain
interface ParentDomain {
  id?: number;
  domain_name?: string;
  domainName?: string;
  [key: string]: any;
}

// Define interface for findings stats
interface FindingsStats {
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
  total?: number;
  [key: string]: any;
}

const Subdomains = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from URL search params
  const [currentPage, setCurrentPage] = React.useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = React.useState(searchParams.get('type') || 'all');
  const [selectedStatus, setSelectedStatus] = React.useState(searchParams.get('status') || 'all');
  const [selectedSeverity, setSelectedSeverity] = React.useState(searchParams.get('severity') || 'all');
  const [selectedTimeframe, setSelectedTimeframe] = React.useState(searchParams.get('timeframe') || 'all');
  // Risk score filter states
  const [selectedRiskScoreMin, setSelectedRiskScoreMin] = React.useState<string>(searchParams.get('riskScore') || '');
  const [selectedRiskScoreMax, setSelectedRiskScoreMax] = React.useState<string>(searchParams.get('riskScoreMax') || '');
  // Findings count filter states
  const [selectedFindingsMin, setSelectedFindingsMin] = React.useState<number | undefined>(
    searchParams.get('findingsMin') ? parseInt(searchParams.get('findingsMin') || '0', 10) : undefined
  );
  const [selectedFindingsMax, setSelectedFindingsMax] = React.useState<number | undefined>(
    searchParams.get('findingsMax') ? parseInt(searchParams.get('findingsMax') || '0', 10) : undefined
  );
  const [selectedOrdering, setSelectedOrdering] = React.useState<string>(searchParams.get('ordering') || 'riskScore');
  const [selectedTags, setSelectedTags] = React.useState<string[]>(
    searchParams.getAll('tag') || []
  );
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(
    searchParams.getAll('region') || []
  );
  const [subdomains, setSubdomains] = React.useState<Subdomain[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = React.useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });

  // Update local state when search parameters change (for back button navigation)
  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const typeParam = searchParams.get('type');
    const statusParam = searchParams.get('status');
    const severityParam = searchParams.get('severity');
    const timeframeParam = searchParams.get('timeframe');
    const tagParams = searchParams.getAll('tag');
    const regionParams = searchParams.getAll('region');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update type if it changed
    if (typeParam !== selectedType) {
      setSelectedType(typeParam || 'all');
    }
    
    // Update status if it changed
    if (statusParam !== selectedStatus) {
      setSelectedStatus(statusParam || 'all');
    }
    
    // Update severity if it changed
    if (severityParam !== selectedSeverity) {
      setSelectedSeverity(severityParam || 'all');
    }
    
    // Update timeframe if it changed
    if (timeframeParam !== selectedTimeframe) {
      setSelectedTimeframe(timeframeParam || 'all');
    }
    
    // Update tags if they changed
    if (JSON.stringify(tagParams) !== JSON.stringify(selectedTags)) {
      setSelectedTags(tagParams || []);
    }
    
    // Update regions if they changed
    if (JSON.stringify(regionParams) !== JSON.stringify(selectedRegions)) {
      setSelectedRegions(regionParams || []);
    }
  }, [searchParams]);

  // Helper function to build URL with search parameters
  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add type if not 'all'
    if (params.type && params.type !== 'all') {
      urlParams.set('type', params.type);
    }
    
    // Add status if not 'all'
    if (params.status && params.status !== 'all') {
      urlParams.set('status', params.status);
    }
    
    // Add severity if not 'all'
    if (params.severity && params.severity !== 'all') {
      urlParams.set('severity', params.severity);
    }
    
    // Add timeframe if not 'all'
    if (params.timeframe && params.timeframe !== 'all') {
      urlParams.set('timeframe', params.timeframe);
    }
    
    // Add tags if present
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => urlParams.append('tag', tag));
    }
    
    // Add regions if present
    if (params.regions && params.regions.length > 0) {
      params.regions.forEach((region: string) => urlParams.append('region', region));
    }
    
    // Add risk score filters if present
    if (params.riskScoreMin) {
      urlParams.set('riskScore', params.riskScoreMin);
    }
    if (params.riskScoreMax) {
      urlParams.set('riskScoreMax', params.riskScoreMax);
    }
    
    // Add findings count filters if present
    if (params.findingsMin !== undefined && params.findingsMin > 0) {
      urlParams.set('findingsMin', params.findingsMin.toString());
    }
    if (params.findingsMax !== undefined && params.findingsMax > 0) {
      urlParams.set('findingsMax', params.findingsMax.toString());
    }
    
    // Add ordering if present
    if (params.ordering) {
      urlParams.set('ordering', params.ordering);
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };

  // Handle page change with URL param update
  const handlePageChange = (page: number) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
    
    // Update the local state
    setCurrentPage(page);
  };

  // Handle search change with URL param update
  const handleSearchChange = (query: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on search
      search: query,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { searchChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle type change
  const handleTypeChange = (type: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedType(type);
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = (status: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  // Handle severity change
  const handleSeverityChange = (severity: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedSeverity(severity);
    setCurrentPage(1);
  };

  // Handle timeframe change
  const handleTimeframeChange = (timeframe: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTimeframe(timeframe);
    setCurrentPage(1);
  };

  // Handle tag change
  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tag] 
      : selectedTags.filter(t => t !== tag);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: newTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  // Handle region change
  const handleRegionChange = (region: string, checked: boolean) => {
    const newRegions = checked 
      ? [...selectedRegions, region] 
      : selectedRegions.filter(r => r !== region);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: newRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedRegions(newRegions);
    setCurrentPage(1);
  };

  // Handle risk score min changes
  const handleRiskScoreMinChange = (value: string) => {
    setSelectedRiskScoreMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: value,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle risk score max changes
  const handleRiskScoreMaxChange = (value: string) => {
    setSelectedRiskScoreMax(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: value,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings min changes
  const handleFindingsMinChange = (value: number | undefined) => {
    setSelectedFindingsMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: value,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings max changes
  const handleFindingsMaxChange = (value: number | undefined) => {
    setSelectedFindingsMax(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: value,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle ordering changes
  const handleOrderingChange = (value: string) => {
    setSelectedOrdering(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScoreMin: selectedRiskScoreMin,
      riskScoreMax: selectedRiskScoreMax,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: value
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle filter reset
  const handleReset = () => {
    // Navigate to base URL without parameters
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
    
    // Reset all local state
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedSeverity('all');
    setSelectedRiskScoreMin('');
    setSelectedRiskScoreMax('');
    setSelectedFindingsMin(undefined);
    setSelectedFindingsMax(undefined);
    setSelectedTimeframe('all');
    setSelectedTags([]);
    setSelectedRegions([]);
    setCurrentPage(1);
  };

  const fetchSubdomains = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      
      // Prepare API request with all filters
      const requestParams: SubdomainsListRequest = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        ordering: selectedOrdering,
      };
      
      // Add risk score filters if selected
      if (selectedRiskScoreMin && selectedRiskScoreMin !== 'no_filter') {
        requestParams.riskScore = selectedRiskScoreMin;
      }
      // if (selectedRiskScoreMax && selectedRiskScoreMax !== 'no_filter') {
      //   requestParams.riskScoreMax = selectedRiskScoreMax;
      // }
      
      // Add findings count filters if selected
      if (selectedFindingsMin !== undefined && selectedFindingsMin > 0) {
        requestParams.findingsMin = selectedFindingsMin;
      }
      if (selectedFindingsMax !== undefined && selectedFindingsMax > 0) {
        requestParams.findingsMax = selectedFindingsMax;
      }
      
      console.log('Fetching subdomains with params:', requestParams);
      const response = await api.subdomainsList(requestParams);
      setSubdomains(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next ?? null,
        previous: response.previous ?? null
      });
    } catch (err) {
      console.error('Error fetching subdomains:', err);
      setError('Failed to load subdomains');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentTeam?.slug, 
    token, 
    currentPage, 
    searchQuery, 
    selectedRiskScoreMin, 
    selectedRiskScoreMax, 
    selectedFindingsMin, 
    selectedFindingsMax,
    selectedOrdering,
  ]);

  const fetchAllSubdomains = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new AssetsApi(getApiConfiguration(token));
    const response = await api.subdomainsList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      ordering: selectedOrdering,
      limit
    });
    return response.results;
  };
  
  React.useEffect(() => {
    fetchSubdomains();
  }, [fetchSubdomains]);

  const transformedSubdomains = subdomains.map((subdomain) => {
    // Parse the parentDomain string to an object
    const parentDomain = parseJsonData<ParentDomain>(subdomain.parentDomain);
    // Parse findings stats
    const findingsStats = parseJsonData<FindingsStats>(subdomain.findingsStats);
    
    return ({
    id: subdomain.id,
    subdomainName: subdomain.subdomainName,
    parentDomain: {
      id: parentDomain?.id?.toString() || '0',
      name: parentDomain?.domain_name || parentDomain?.domainName || 'Unknown'
    },
    isActive: subdomain.isActive || false,
    riskScore: (subdomain.riskScore || 'n/a').toUpperCase(),
    updatedAt: new Date(subdomain.updatedAt).toLocaleString(),
    vulnerabilities: {
      critical: findingsStats?.critical || 0,
      high: findingsStats?.high || 0,
      medium: findingsStats?.medium || 0,
      low: findingsStats?.low || 0
    },
    createdAt: new Date(subdomain.createdAt).toLocaleString(),
    tags: subdomain.tags || [],
    technologies: parseJsonData<any[]>(subdomain.technologies)?.map((tech: any) => ({
      name: tech.name || 'Unknown',
      version: tech.version || 'Unknown',
      logo_path: tech.logo_path || '',
      color: tech.color || 'gray'
    })) || [],
    dnsRecords: subdomain.dnsRecords || [],
    tlsCertificate: subdomain.tlsCertificate || null,
    ipAddresses: subdomain.ipAddresses || []
  });
  });

  // Metrics for subdomains page
  const subdomainMetrics = {
    web: subdomains.length,
    servers: 0,
    databases: 0,
    network: 0,
    vulnerable: subdomains.filter(s => !s.isActive).length,
    secure: subdomains.filter(s => s.isActive).length,
    total: subdomains.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Network className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Subdomains</h1>
            <p className="text-sm text-gray-400">Monitor and manage your subdomain assets</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ExportActionBtn
            fetchAllData={() => fetchAllSubdomains(paginationInfo.count)}
            filename="subdomains"
            disabled={subdomains?.length === 0}
          />
          <AddAssetButton onClick={() => console.log('Adding new subdomain...')} />
        </div>
      </div>

      {/* Metrics Overview 
      <AssetsMetrics metrics={subdomainMetrics} />
*/}
      {/* Tabs */}
      <AssetTabs />

      {/* Filters */}
      <AssetsFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedSeverity={selectedSeverity}
        onSeverityChange={handleSeverityChange}
        selectedRiskScoreMin={selectedRiskScoreMin}
        onRiskScoreMinChange={handleRiskScoreMinChange}
        selectedRiskScoreMax={selectedRiskScoreMax}
        onRiskScoreMaxChange={handleRiskScoreMaxChange}
        selectedFindingsMin={selectedFindingsMin}
        onFindingsMinChange={handleFindingsMinChange}
        selectedFindingsMax={selectedFindingsMax}
        onFindingsMaxChange={handleFindingsMaxChange}
        selectedOrdering={selectedOrdering}
        onOrderingChange={handleOrderingChange}
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedRegions={selectedRegions}
        onRegionChange={handleRegionChange}
        onReset={handleReset}
        onExport={() => {
          console.log('Exporting subdomains...');
        }}
      />
      
      {/* Parent Domain Filter */}
      {/* <div className="mt-4">
        <div className="w-full max-w-xs">
          <label htmlFor="parent-domain" className="block text-sm font-medium text-gray-400 mb-1">
            Parent Domain
          </label>
          <input
            id="parent-domain"
            type="text"
            value={selectedParentDomain}
            onChange={(e) => {
              setSelectedParentDomain(e.target.value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            placeholder="Filter by parent domain..."
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div> */}

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading subdomains</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <SubdomainsTable
          subdomains={transformedSubdomains}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalSubdomains={paginationInfo.count}
          itemsPerPage={10}
          paginationInfo={paginationInfo}
        />
      )}
    </div>
  );
};

export default Subdomains;